import { ABTestAssignments } from '@play-co/replicant/lib/core/api/ReplicantAPI';
import { DynamicABTestBucketID, DynamicABTestID } from '../ruleset/ab';
import { MutableState, State, stateSchema } from '../State';
import { SyncActionAPI } from '../utils/types';
import { isGuestUser } from './guest.getters';

/**
 * Restore state from local storage on first login.
 * This is used to migrate from offline guest mode to online guest mode.
 *
 * @param state - The current state.
 * @param localState - The local state to restore from.
 */
export function restoreGuestStateFromLocalStorage(
  state: MutableState,
  localState: string,
  api: SyncActionAPI,
): void {
  // Replicant is offline, bail out.
  if (process.env.REPLICANT_OFFLINE === 'true') return;

  // Not in guest mode, bail out.
  if (!isGuestUser(state)) return;

  // Not the first session, bail out.
  if (state.lastSessionAt) return;

  // Try parsing the local state and assigning it to the current state.
  try {
    // This is the type we expect the local state to have.
    type ParsedState = {
      state: MutableState;
      metainfo: {
        abTestAssignments: ABTestAssignments;
      };
    };

    // Parse the local state and cast it to the expected type.
    const {
      state: parsedState,
      metainfo: { abTestAssignments },
    } = JSON.parse(localState) as ParsedState;

    // Bail if parsed state doesn't match the current state's schema.
    const schemaValidationError = stateSchema.validate(parsedState);
    if (schemaValidationError) {
      throw new Error(
        `Could not restore guest mode state: ${schemaValidationError}`,
      );
    }

    // Restore the state from the parsed state.
    restoreFromState(state, parsedState, abTestAssignments, api);
  } catch (e: any) {
    api.reportError(e);
  }
}

/**
 * Restore progress from another state object.
 * This is used to restore progress in guest mode.
 *
 * @param state - The current state.
 * @param fromState - The state to restore from.
 * @param abTestAssignments - The ab test assignments to restore.
 * @param api - The API to use for restoring the state.
 */
export function restoreFromState(
  state: MutableState,
  fromState: State,
  abTestAssignments: ABTestAssignments,
  api: SyncActionAPI,
): void {
  // Get the default state from the schema. This is to avoid copying over meta fields.
  // The default state only containes fields set by the game.
  const defaultState = stateSchema.getDefault();

  // Not actually possible, but this is a type guard.
  if (!defaultState) return;

  // Alias type for default keys.
  type DefaultStateKeys = keyof typeof defaultState;

  // Analytics properties, will be merged appropriately if necessary.
  const analyticsProperties: DefaultStateKeys[] = [
    'analytics',
    'platformStorage',
    'lifetimeValue',
    'actualLTV',
  ];

  // These properties shouldn't be copied over from the new state.
  const blacklistedProperties: DefaultStateKeys[] = [
    ...analyticsProperties,
    // Keep the original pending payments, since the purchase history is not being carried over.
    'pendingPayments',
  ];

  // Keep track of the original values of blacklisted properties.
  const originalValues = blacklistedProperties.reduce(
    (acc, rawKey) => {
      const key = rawKey as DefaultStateKeys;
      acc[key] = state[key];
      return acc;
    },
    {} as Record<DefaultStateKeys, unknown>,
  );

  // Copy over non-blacklisted properties from the new state.
  Object.keys(defaultState).forEach((rawKey) => {
    const key = rawKey as DefaultStateKeys;
    if (blacklistedProperties.includes(key)) return;

    // TODO: Fix weird cast to get around TS error.
    state[key] = fromState[key] as never;
  });

  // Copy analytics properties from the new state if it was created before the current state.
  // This is to always preserve the oldest analytics properties.
  if (fromState.createdAt < state.createdAt) {
    analyticsProperties.forEach((key) => {
      state[key] = originalValues[key] as never;
    });
  }

  // Assign ab tests to the current state.
  Object.keys(abTestAssignments).forEach((key) => {
    const typedKey = key as Parameters<typeof api.abTests.assign>[0];
    const assignment = abTestAssignments[typedKey];
    const bucketId =
      assignment && 'bucketId' in assignment ? assignment.bucketId : undefined;

    // No bucketId means the test is not assigned.
    if (!bucketId) {
      api.abTests.unassign(typedKey);
      return;
    }
    // Assign the test to the current state.
    api.abTests.assign(typedKey, bucketId as DynamicABTestBucketID);
  });
}
